// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import {syncLocalVoiceStateWithServer} from '@app/features/voice/engine/VoiceMediaEngineBridge';
import {asPinnableVoiceTrackSource, VoiceTrackSource} from '@app/features/voice/engine/VoiceTrackSource';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';

type VoiceMediaSource = 'camera' | 'screen_share';
type VoiceMediaStateReason =
	| 'user'
	| 'server'
	| 'track_event'
	| 'disconnect'
	| 'room_disconnect'
	| 'cleanup'
	| 'logout';

interface VoiceMediaStateOptions {
	sendUpdate?: boolean;
	reason: VoiceMediaStateReason;
}

const logger = new Logger('VoiceMediaStateCoordinator');

class VoiceMediaStateCoordinator {
	private suppressNextSync: Record<VoiceMediaSource, boolean> = {
		camera: false,
		screen_share: false,
	};

	applyCameraState(enabled: boolean, options: VoiceMediaStateOptions): void {
		this.applyRequestedState('camera', enabled, options);
	}

	applyScreenShareState(enabled: boolean, options: VoiceMediaStateOptions): void {
		this.applyRequestedState('screen_share', enabled, options);
	}

	handleLocalTrackStateChange(source: unknown, isPublished: boolean): boolean {
		const mapped = this.mapSource(source);
		if (!mapped) return false;
		const suppressed = this.consumeSuppression(mapped);
		const changed = this.updateLocalState(mapped, isPublished);
		if (suppressed || !changed) {
			if (suppressed) {
				logger.debug('Suppressed local track sync', {source: mapped, isPublished});
			}
			return changed;
		}
		this.syncState(mapped, isPublished);
		return changed;
	}

	resetLocalMediaState(reason: VoiceMediaStateReason): void {
		const suppress =
			reason === 'disconnect' || reason === 'room_disconnect' || reason === 'cleanup' || reason === 'logout';
		this.suppressNextSync.camera = suppress;
		this.suppressNextSync.screen_share = suppress;
		this.applyRequestedState('camera', false, {reason, sendUpdate: false});
		this.applyRequestedState('screen_share', false, {reason, sendUpdate: false});
	}

	private applyRequestedState(source: VoiceMediaSource, enabled: boolean, options: VoiceMediaStateOptions): void {
		const {sendUpdate = true, reason} = options;
		if (reason === 'user') {
			this.suppressNextSync[source] = false;
		}
		if (!sendUpdate) {
			this.suppressNextSync[source] = true;
		}
		const changed = this.updateLocalState(source, enabled);
		if (!sendUpdate || !changed) return;
		this.syncState(source, enabled);
	}

	private updateLocalState(source: VoiceMediaSource, enabled: boolean): boolean {
		if (source === 'camera') {
			const current = LocalVoiceState.getSelfVideo();
			if (current === enabled) return false;
			LocalVoiceState.updateSelfVideo(enabled);
			return true;
		}
		const current = LocalVoiceState.getSelfStream();
		if (current === enabled) return false;
		LocalVoiceState.updateSelfStream(enabled);
		return true;
	}

	private syncState(source: VoiceMediaSource, enabled: boolean): void {
		if (!this.canSync()) return;
		if (source === 'camera') {
			syncLocalVoiceStateWithServer({self_video: enabled});
			return;
		}
		syncLocalVoiceStateWithServer({self_stream: enabled});
	}

	private canSync(): boolean {
		if (VoiceConnectionManager.disconnecting) return false;
		const {channelId, connectionId} = VoiceConnectionManager.connectionState;
		return Boolean(channelId && connectionId);
	}

	private mapSource(source: unknown): VoiceMediaSource | null {
		const pinnedSource = asPinnableVoiceTrackSource(source);
		if (pinnedSource === VoiceTrackSource.Camera) return 'camera';
		if (pinnedSource === VoiceTrackSource.ScreenShare) return 'screen_share';
		return null;
	}

	private consumeSuppression(source: VoiceMediaSource): boolean {
		if (!this.suppressNextSync[source]) return false;
		this.suppressNextSync[source] = false;
		return true;
	}
}

export default new VoiceMediaStateCoordinator();
